import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TopBar from './TopBar';
import './VendorReviewsPage.css';
import { getReviewsByVendorId, getAverageRatingByVendorId } from '../services/reviewService';
import { getProfileByUserId } from '../services/profileService';

const VendorReviewsPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewerMap, setReviewerMap] = useState({});

  const { user, profile: currentProfile, isAuthenticated } = useAuth();

  useEffect(() => {
    // If the user is trying to view their own vendor profile, ensure they are logged in and have vendor role
    if (vendorId && user && vendorId === user.$id && (!isAuthenticated || currentProfile?.role !== 'vendor')) {
      // Not allowed to view own Profile page if not a logged-in vendor
      navigate('/', { replace: true });
      return;
    }

    const load = async () => {
      if (!vendorId) {
        setError('Vendor not specified.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [profile, revs, avg] = await Promise.all([
          getProfileByUserId(vendorId).catch(() => null),
          getReviewsByVendorId(vendorId).catch(() => []),
          getAverageRatingByVendorId(vendorId).catch(() => null)
        ]);

        setVendor(profile);
        setReviews(revs || []);
        setAvgRating(avg);

        // Load reviewer display names for each review (to show "by <name>")
        try {
          const revsArr = revs || [];
          const reviewerIds = Array.from(new Set(revsArr.map(r => r.reviewer_id).filter(Boolean)));
          const map = {};
          await Promise.all(reviewerIds.map(async (id) => {
            try {
              const p = await getProfileByUserId(id);
              map[id] = (p && p.full_name) ? p.full_name : 'Unknown';
            } catch (err) {
              map[id] = 'Unknown';
            }
          }));
          setReviewerMap(map);
        } catch (err) {
          console.warn('Failed to load reviewer names:', err);
        }
      } catch (err) {
        console.error('Error loading vendor reviews:', err);
        setError('Failed to load reviews.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [vendorId]);

  const vendorName = (vendor && (vendor.vendor_name || vendor.full_name)) || 'Vendor';
  const pageTitle = vendorName.endsWith('s') ? `${vendorName}' Reviews` : `${vendorName}'s Reviews`;

  return (
    <div className="vendor-reviews-page">
      <TopBar />

      <div className="vendor-reviews-container">
        <button className="back-link" onClick={() => navigate(-1)}>← Back</button>

        {loading ? (
          <div className="loading">Loading reviews…</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="vendor-header">
              <h1 className="vendor-name">{pageTitle}</h1>
              {avgRating !== null && (
                <div className="vendor-rating">⭐ {avgRating} <span className="small">({reviews.length} reviews)</span></div>
              )}
            </div>

            {reviews.length === 0 ? (
              <div className="no-reviews">No reviews yet for this vendor.</div>
            ) : (
              <div className="reviews-list">
                {reviews.map((r) => (
                  <div key={r.$id} className="review-card">
                    <div className="review-row">
                      <div className="review-title"><strong>{r.title || 'Untitled'}</strong></div>
                      <div className="review-rating">⭐ {r.rating}</div>
                    </div>
                    <div className="review-body">{r.description}</div>
                    <div className="review-meta">{new Date(r.$createdAt).toLocaleString()} • by {reviewerMap[r.reviewer_id] || 'Reviewer'}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>


    </div>
  );
};

export default VendorReviewsPage;
